import {
  type HistoryThreadRef,
  historyThreadKey,
} from "@/features/history/historyIdentity";
import {
  type HistoryMode,
  parseHistoryMode,
} from "@/features/history/historyMode";

export interface DesktopHistorySidebarConfig {
  sidebarVisible: boolean;
  sortByNewRes: boolean;
  historyMode: HistoryMode;
}

export const DESKTOP_HISTORY_MAX_ITEMS = 50;
/** 固定幅サイドバーに収まるようDesktopCatalogItemへ渡す縮小画像サイズ(px) */
export const DESKTOP_HISTORY_ITEM_IMAGE_SIZE = 50;
/** 履歴サイドバーはカタログ共通設定に連動させず、本文を2行で表示する。 */
export const DESKTOP_HISTORY_ITEM_TEXT_LINES = 2;
export const DEFAULT_DESKTOP_HISTORY_CONFIG: DesktopHistorySidebarConfig = {
  sidebarVisible: false,
  sortByNewRes: false,
  historyMode: "viewed",
};

export function parseDesktopHistorySidebarConfig(
  storedValue: string | null,
): DesktopHistorySidebarConfig {
  try {
    const raw: unknown = JSON.parse(storedValue ?? "null");
    if (!isRecord(raw)) return { ...DEFAULT_DESKTOP_HISTORY_CONFIG };
    return {
      sidebarVisible:
        typeof raw.sidebarVisible === "boolean"
          ? raw.sidebarVisible
          : typeof raw.visible === "boolean"
            ? raw.visible
            : DEFAULT_DESKTOP_HISTORY_CONFIG.sidebarVisible,
      sortByNewRes:
        typeof raw.sortByNewRes === "boolean"
          ? raw.sortByNewRes
          : DEFAULT_DESKTOP_HISTORY_CONFIG.sortByNewRes,
      historyMode: parseHistoryMode(raw.historyMode),
    };
  } catch {
    return { ...DEFAULT_DESKTOP_HISTORY_CONFIG };
  }
}

export function selectDesktopHistoryItems<T>(viewed: readonly T[]): T[] {
  return viewed.slice(0, DESKTOP_HISTORY_MAX_ITEMS);
}

export type DesktopHistorySidebarContentState =
  | "empty"
  | "loading"
  | "error"
  | "noExistingThreads"
  | "ready";

export function resolveDesktopHistorySidebarContentState({
  historyCount,
  isLoading,
  hasError,
  threadCount,
}: {
  historyCount: number;
  isLoading: boolean;
  hasError: boolean;
  threadCount: number;
}): DesktopHistorySidebarContentState {
  if (historyCount === 0) return "empty";
  if (isLoading) return "loading";
  if (threadCount > 0) return "ready";
  if (hasError) return "error";
  return "noExistingThreads";
}

export function sortDesktopHistoryThreads<
  T extends HistoryThreadRef & { replyCount: number },
>(
  threads: readonly T[],
  historyItems: readonly HistoryThreadRef[],
  sortByNewRes: boolean,
  getUnreadCount: (
    boardSlug: string,
    threadId: string,
    repliesCount: number,
  ) => number | undefined,
): T[] {
  const historyPositions = new Map(
    historyItems.map((item, index) => [historyThreadKey(item), index]),
  );
  const historyPosition = (item: HistoryThreadRef): number =>
    historyPositions.get(historyThreadKey(item)) ?? historyItems.length;

  return [...threads].sort((left, right) => {
    const historyOrder = historyPosition(left) - historyPosition(right);
    if (!sortByNewRes) return historyOrder;
    const unreadOrder =
      (getUnreadCount(right.boardSlug, right.threadId, right.replyCount) ?? 0) -
      (getUnreadCount(left.boardSlug, left.threadId, left.replyCount) ?? 0);
    return unreadOrder || historyOrder;
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
